/**
 * Manages the queue for the postMessage calls to the Emersya viewer.
 *
 * Some actions need to be executed in order. The EmersyaQueue allows us to
 * push callbacks into a queue identified by an ID, such id can either be
 * generated with the EmersyaQueue.randomId() method or chosen arbitrarily.
 *
 * After pushing our callbacks into the Queue we can start the de-queue
 * by calling emersyaQueueInstance.run(queueId). The first callback in the
 * queue will be immediately invoked and each subsequent callback will be
 * executed after the previous one has been completed, which means at every
 * 'message' event received on the `window` object with the same "id" property
 * value.
 *
 * Example:
 *   const queue = new EmersyaQueue();
 *
 *   window.addEventListener('message', event => {
 *     queue.stateChanged(event.data.id, event.data);
 *   });
 *
 *   const queueId = EmersyaQueue.randomId();
 *   queue.queue(() => {
 *     this.postMessageToViewer({ id: queueId, action: 'emersyaAction' }, '*');
 *   });
 *
 *   queue.queue(() => {
 *     this.postMessageToViewer({ id: queueId, action: 'emersyaAction' }, '*');
 *   });
 *
 *   queue.run(queueId);
 */

import { randomId as _randomId } from './Helpers';

class EmersyaQueue {
  constructor() {
    this._queue = {};
  }


  /**
   * Add an item to the queue
   *
   * @param callback
   * @param {number|string} id
   * @param {string|null} callAction
   * @returns {number}
   */
  queue(callback, id, callAction) {
    if ( ! this._queue[id]) {
      this._queue[id] = [];
    }

    this._queue[id].push({ callAction, callback });

    return id;
  }

  /**
   * Invoke the last callback pushed into the queue
   *
   * @param {number} id
   * @param {*} data
   * @returns {*}
   */
  run(id, data = null) {
    // We need at least an item in the queue
    if ( ! (this._queue[id] && this._queue[id].length > 0)) {
      return false;
    }

    this._queue[id][0].callback(data);
  }

  stateChanged(id, data) {
    // We need at least an item in the queue
    if ( ! (this._queue[id] && this._queue[id].length > 0)) {
      return false;
    }

    const item = this._queue[id][0];

    if (data && data.callAction === item.callAction) {
      this._queue[id].shift();
      this.run(id);

      // console.log('Queue has progressed because a matching callAction', item, data);
    } else {
      // console.warn('Queue has elements but item can not run because of non matching callAction', item, data);
    }
  }

  /**
   * @deprecated Use the one in Helpers. This one is only for backward
   *   compatibility.
   * @returns {*}
   */
  static randomId() {
    return _randomId();
  }
}

export default EmersyaQueue;



// WEBPACK FOOTER //
// ./src/EmersyaQueue.js